<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, nextTick, reactive, ref, watch, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import UnuCalendar from './UnuCalendar.vue';
import {
  normalizeTab,
  parseMessages,
  statuses,
  tabs,
  validateTask,
  errorText,
} from './model';

const route = useRoute();
const router = useRouter();
const tab = computed(() => normalizeTab(route.query.tab));
const groups = ref([]);
const calendarTasks = ref([]);
const calendarRevision = ref(0);
const calendarWarning = ref(false);
let calendarSignature = '';
const connection = ref(null);
const configured = ref(false);
const groupId = ref('');
const group = computed(() =>
  groups.value.find(item => item.id === groupId.value)
);
const data = reactive({ tasks: [], messages: [], workers: [] });
const loading = ref(true);
const groupLoading = ref(false);
const denied = ref(false);
const error = ref('');
const notice = ref('');
const saving = ref(false);
const search = ref('');
const view = ref('list');
const page = ref(1);
const pageCount = ref(1);
const tasksPage = ref(1);
const taskPages = ref(1);
const messageTotal = ref(0);
const formError = ref('');
const importError = ref('');
const importText = ref('');
const taskDialog = ref(null);
const importDialog = ref(null);
const historyDialog = ref(null);
const selectedTask = ref(null);
const history = ref(null);
const historyLoading = ref(false);
const editingId = ref(null);
const formKey = ref('');
const importKey = ref('');
const settingKey = ref('');
const form = reactive({
  text: '',
  plannedAt: '',
  gender: '1',
  age: '18',
  messageId: '',
  lock_version: null,
});
const groupSettings = ref({
  enabled: true,
  period_weeks: 1,
  poll_seconds: 30,
  lock_version: null,
});
const available = computed(
  () =>
    configured.value &&
    connection.value?.enabled &&
    !!group.value &&
    !groupLoading.value &&
    !loading.value &&
    !denied.value
);
const tasks = computed(() => data.tasks);
const messages = computed(() => data.messages);
const pagedMessages = computed(() => data.messages);
const workers = computed(() => data.workers);
const priceLabel = computed(() =>
  connection.value?.price_minor
    ? new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
      }).format(connection.value.price_minor / 100)
    : '—'
);
const balanceLabel = computed(() =>
  connection.value?.balance != null
    ? new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        maximumFractionDigits: 2,
      }).format(Number(connection.value.balance))
    : '—'
);
let epoch = 0;
let timer = null;
function key() {
  return crypto.randomUUID();
}
const api = (path, method = 'get', body = null) =>
  axios({
    url: `/api/v1/accounts/${route.params.accountId}/tg_unu${path}`,
    method,
    ...(body ? { data: body } : {}),
  });
function handleError(e) {
  if (e.response?.status === 403) denied.value = true;
  error.value = errorText(e);
}
async function loadGroup() {
  const id = groupId.value;
  if (!id) return;
  epoch += 1;
  const current = epoch;
  groupLoading.value = true;
  error.value = '';
  data.tasks = [];
  calendarTasks.value = [];
  calendarSignature = '';
  data.messages = [];
  data.workers = [];
  try {
    const base = `group_id=${encodeURIComponent(id)}`;
    const [s, t, m, w] = await Promise.all([
      api(`/settings?${base}`),
      api(`/tasks?${base}&page=${tasksPage.value}`),
      api(
        `/messages?${base}&page=${page.value}&query=${encodeURIComponent(search.value)}`
      ),
      api(`/workers?${base}`),
    ]);
    if (current !== epoch) return;
    groupSettings.value = s.data;
    data.tasks = t.data.items;
    taskPages.value = t.data.total_pages;
    tasksPage.value = t.data.page;
    data.messages = m.data.items;
    messageTotal.value = m.data.total;
    pageCount.value = m.data.total_pages;
    page.value = m.data.page;
    data.workers = w.data.items;
    calendarRevision.value += 1;
    settingKey.value = key();
  } catch (e) {
    if (current === epoch) handleError(e);
  } finally {
    if (current === epoch) groupLoading.value = false;
  }
}
async function bootstrap() {
  clearTimeout(timer);
  groupId.value = '';
  groups.value = [];
  connection.value = null;
  data.tasks = [];
  data.messages = [];
  data.workers = [];
  epoch += 1;
  const current = epoch;
  loading.value = true;
  error.value = '';
  denied.value = false;
  try {
    const { data: result } = await api('/bootstrap');
    if (current !== epoch) return;
    groups.value = result.groups;
    connection.value = result.connection;
    configured.value = result.configured;
    groupId.value =
      groups.value.find(item => item.id === String(route.query.group))?.id ||
      groups.value[0]?.id ||
      '';
  } catch (e) {
    if (current === epoch) handleError(e);
  } finally {
    if (current === epoch) loading.value = false;
  }
  if (current === epoch && groupId.value) await loadGroup();
}
function selectTab(item) {
  router.replace({
    query: {
      tab: item.key,
      ...(groupId.value ? { group: groupId.value } : {}),
    },
  });
  notice.value = '';
}
async function changeGroup() {
  tasksPage.value = 1;
  page.value = 1;
  search.value = '';
  notice.value = '';
  taskDialog.value?.close();
  importDialog.value?.close();
  historyDialog.value?.close();
  await router.replace({ query: { tab: tab.value, group: groupId.value } });
  await loadGroup();
}
watch(
  () => route.params.accountId,
  () => {
    taskDialog.value?.close();
    importDialog.value?.close();
    historyDialog.value?.close();
    bootstrap();
  },
  { immediate: true }
);
watch([page, tasksPage], () => {
  if (!groupLoading.value) loadGroup();
});
watch(search, () => {
  clearTimeout(timer);
  timer = setTimeout(() => {
    page.value = 1;
    loadGroup();
  }, 300);
});
onBeforeUnmount(() => {
  epoch += 1;
  clearTimeout(timer);
});
function newTask(task = null) {
  editingId.value = task?.id || null;
  Object.assign(
    form,
    task
      ? {
          text: task.text,
          plannedAt: task.plannedAt,
          gender: String(task.gender),
          age: task.age == null ? '' : String(task.age),
          messageId: '',
          lock_version: task.lock_version,
        }
      : {
          text: '',
          plannedAt: '',
          gender: '1',
          age: '18',
          messageId: '',
          lock_version: null,
        }
  );
  formKey.value = key();
  formError.value = '';
  taskDialog.value?.open();
}
function pickMessage() {
  form.text =
    messages.value.find(item => String(item.id) === String(form.messageId))
      ?.text || '';
}
async function saveTask() {
  if (saving.value) return;
  formError.value = validateTask(form);
  if (formError.value) return;
  saving.value = true;
  const body = {
    request_key: formKey.value,
    group_id: groupId.value,
    text: form.text.trim(),
    planned_at: form.plannedAt,
    gender: Number(form.gender),
    age: form.age === '' ? null : Number(form.age),
    price_minor: connection.value.price_minor,
    lock_version: form.lock_version,
    ...(!editingId.value && form.messageId
      ? { message_id: form.messageId }
      : {}),
  };
  try {
    await api(
      editingId.value ? `/tasks/${editingId.value}` : '/tasks',
      editingId.value ? 'patch' : 'post',
      body
    );
    taskDialog.value?.close();
    notice.value =
      'Задание сохранено. Оно будет передано в UNU не раньше указанного времени. Результат появится в истории.';
    await loadGroup();
  } catch (e) {
    formError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
async function addTexts() {
  const result = parseMessages(importText.value);
  importError.value = result.error;
  if (result.error || saving.value) return;
  saving.value = true;
  importKey.value ||= key();
  try {
    await api('/messages', 'post', {
      request_key: importKey.value,
      group_id: groupId.value,
      text: importText.value,
    });
    importDialog.value?.close();
    importText.value = '';
    importKey.value = '';
    notice.value = 'Тексты сохранены в библиотеке группы.';
    page.value = 1;
    await loadGroup();
  } catch (e) {
    importError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
async function openHistory(task) {
  selectedTask.value = task;
  history.value = null;
  historyLoading.value = true;
  await nextTick();
  historyDialog.value?.open();
  const id = groupId.value;
  try {
    const result = await api(`/tasks/${task.id}/history`);
    if (groupId.value === id) history.value = result.data;
  } catch (e) {
    handleError(e);
  } finally {
    historyLoading.value = false;
  }
}
async function saveSettings() {
  if (saving.value) return;
  saving.value = true;
  try {
    const result = await api('/settings', 'patch', {
      request_key: settingKey.value,
      group_id: groupId.value,
      ...groupSettings.value,
      period_weeks: Number(groupSettings.value.period_weeks),
      poll_seconds: Number(groupSettings.value.poll_seconds),
    });
    groupSettings.value = result.data;
    settingKey.value = key();
    notice.value = 'Настройки группы сохранены.';
  } catch (e) {
    handleError(e);
  } finally {
    saving.value = false;
  }
}
async function sync() {
  if (saving.value) return;
  saving.value = true;
  try {
    await api('/sync', 'post', {});
    notice.value =
      'Синхронизация поставлена в очередь. Обновите данные через несколько секунд.';
  } catch (e) {
    handleError(e);
  } finally {
    saving.value = false;
  }
}
function failureText(code) {
  return errorText({ response: { data: { error: code } } });
}
async function loadCalendar(range) {
  const selectedGroupId = groupId.value;
  const signature = JSON.stringify([selectedGroupId, range.start, range.end]);
  if (signature === calendarSignature) return;
  calendarSignature = signature;
  try {
    const result = await api(
      `/calendar?group_id=${selectedGroupId}&from=${encodeURIComponent(range.start)}&to=${encodeURIComponent(range.end)}`
    );
    if (selectedGroupId === groupId.value && signature === calendarSignature) {
      calendarTasks.value = result.data.items;
      calendarWarning.value = result.data.truncated;
    }
  } catch (e) {
    calendarSignature = '';
    handleError(e);
  }
}
async function cancelTask(task) {
  if (saving.value) return;
  saving.value = true;
  try {
    await api(`/tasks/${task.id}/cancel`, 'post', {
      request_key: key(),
      lock_version: task.lock_version,
    });
    notice.value = 'Задание отменено до отправки в UNU.';
    await loadGroup();
  } catch (e) {
    handleError(e);
  } finally {
    saving.value = false;
  }
}
function localTime(value) {
  return value?.replace('T', ' в ') || '—';
}
const reportStates = {
  1: 'В работе',
  2: 'На проверке',
  3: 'На доработке',
  4: 'Отклонён',
  5: 'Отменён',
  6: 'Оплачен',
};
const eventLabels = {
  scheduled: 'Задание сохранено',
  edited: 'Задание изменено',
  cancelled: 'Задание отменено',
  dispatch_attempting: 'Начата передача в UNU',
  submitted: 'Заказ создан в UNU',
  dispatch_unknown: 'Результат отправки неизвестен — повтор отключён',
  provider_rejected: 'UNU отклонил запрос',
  legacy_imported: 'Перенесено из старой системы',
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="UNU"
        description="Задания и сообщения для Telegram-групп"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 min-w-0">
        <Banner v-if="denied" color="ruby">
          Нет доступа. Раздел доступен администратору кабинета.
        </Banner>
        <p v-else-if="loading" role="status" class="text-n-slate-11">
          Загрузка раздела UNU…
        </p>
        <div v-else-if="error && !groups.length" class="flex flex-col gap-3">
          <Banner color="ruby">
            {{ error }}
          </Banner>
          <Button label="Повторить" size="sm" @click="bootstrap" />
        </div>
        <template v-else>
          <Banner v-if="!available" color="slate">
            {{
              configured
                ? 'Подключение UNU временно недоступно.'
                : 'UNU не подключён для этого кабинета.'
            }}
          </Banner>
          <div
            class="flex flex-col sm:flex-row sm:items-end gap-4 justify-between"
          >
            <label
              class="flex flex-col gap-2 text-sm text-n-slate-12 min-w-0 sm:max-w-md"
            >
              Группа
              <select
                v-model="groupId"
                aria-label="Группа UNU"
                class="w-full min-w-0"
                @change="changeGroup"
              >
                <option v-for="item in groups" :key="item.id" :value="item.id">
                  {{ item.title }}
                </option>
              </select>
            </label>
            <div class="text-sm text-n-slate-11">
              Баланс UNU
              <span class="font-medium text-n-slate-12">{{
                balanceLabel
              }}</span>
              <p class="text-xs m-0">
                {{
                  connection?.checked_at
                    ? 'По последней синхронизации UNU'
                    : 'Ожидаем синхронизацию'
                }}
              </p>
            </div>
          </div>
          <div class="overflow-x-auto pb-1">
            <TabBar
              :tabs="tabs"
              :initial-active-tab="tabs.findIndex(item => item.key === tab)"
              @tab-changed="selectTab"
            />
          </div>
          <Banner v-if="error" color="ruby" role="alert">{{ error }}</Banner>
          <p v-if="groupLoading" role="status" class="text-sm text-n-slate-11">
            Обновляем данные группы…
          </p>
          <Banner v-if="notice" role="status" color="blue">{{ notice }}</Banner>
          <section
            v-if="tab === 'tasks'"
            class="flex flex-col gap-4 min-w-0"
            aria-label="Задания UNU"
          >
            <div class="flex flex-wrap gap-2 justify-between items-center">
              <h2 class="text-base font-medium text-n-slate-12 m-0">
                Задания группы
              </h2>
              <Button
                label="Новое задание"
                icon="i-lucide-plus"
                size="sm"
                :disabled="!available || saving"
                @click="newTask()"
              />
            </div>
            <div class="flex flex-wrap gap-2 items-center">
              <Button
                label="Список"
                size="sm"
                :variant="view === 'list' ? 'solid' : 'outline'"
                @click="view = 'list'"
              />
              <Button
                label="Календарь"
                size="sm"
                :variant="view === 'calendar' ? 'solid' : 'outline'"
                @click="view = 'calendar'"
              />
              <span class="text-xs text-n-slate-11"
                >Время — Москва (UTC+3). Следующее задание ожидает взятия
                предыдущего.</span
              >
            </div>
            <Banner v-if="view === 'calendar' && calendarWarning" color="amber">
              Показаны первые 500 заданий. Выберите более короткий период.
            </Banner>
            <UnuCalendar
              v-if="view === 'calendar'"
              :key="groupId"
              :tasks="calendarTasks"
              :refresh-key="calendarRevision"
              :disabled="!available || saving"
              @select="openHistory"
              @range="loadCalendar"
              @create="newTask()"
            />
            <div
              v-if="view === 'list' && tasks.length"
              class="overflow-x-auto border border-n-weak rounded-xl"
            >
              <table class="w-full text-sm text-left">
                <thead class="bg-n-alpha-1 text-n-slate-11">
                  <tr>
                    <th class="p-3 font-medium">Текст</th>
                    <th class="p-3 font-medium">Дата и время, МСК</th>
                    <th class="p-3 font-medium">Статус</th>
                    <th class="p-3 font-medium">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="task in tasks"
                    :key="task.id"
                    class="border-t border-n-weak"
                  >
                    <td class="p-3 min-w-48 max-w-sm break-words">
                      {{ task.text }}
                      <p
                        v-if="task.error_code"
                        class="text-n-ruby-9 mt-1 mb-0 text-xs"
                      >
                        {{ failureText(task.error_code) }}
                      </p>
                    </td>
                    <td class="p-3 whitespace-nowrap text-n-slate-11">
                      {{ localTime(task.plannedAt) }}
                    </td>
                    <td class="p-3 text-n-slate-12">
                      {{ statuses[task.state] }}
                    </td>
                    <td class="p-3">
                      <div class="flex gap-2">
                        <Button
                          label="История"
                          size="sm"
                          variant="ghost"
                          @click="openHistory(task)"
                        /><Button
                          v-if="
                            ['scheduled', 'waiting', 'failed'].includes(
                              task.state
                            ) &&
                            !task.attempted_at &&
                            !task.provider_task_id
                          "
                          label="Изменить"
                          size="sm"
                          variant="ghost"
                          :disabled="!available || saving"
                          @click="newTask(task)"
                        /><Button
                          v-if="
                            ['scheduled', 'waiting', 'failed'].includes(
                              task.state
                            ) &&
                            !task.attempted_at &&
                            !task.provider_task_id
                          "
                          label="Отменить"
                          size="sm"
                          variant="ghost"
                          :disabled="saving"
                          @click="cancelTask(task)"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p
              v-if="view === 'list' && !tasks.length && !groupLoading"
              class="text-sm text-n-slate-11 py-12 text-center"
            >
              Заданий пока нет. Создайте первое задание.
            </p>
            <div class="flex gap-3 items-center">
              <Button
                label="Предыдущие задания"
                size="sm"
                variant="outline"
                :disabled="tasksPage <= 1 || groupLoading"
                @click="tasksPage -= 1"
              /><span class="text-xs text-n-slate-11"
                >{{ tasksPage }} / {{ taskPages }}</span
              ><Button
                label="Следующие задания"
                size="sm"
                variant="outline"
                :disabled="tasksPage >= taskPages || groupLoading"
                @click="tasksPage += 1"
              />
            </div>
          </section>
          <section
            v-else-if="tab === 'messages'"
            class="flex flex-col gap-4"
            aria-label="Тексты UNU"
          >
            <div class="flex flex-wrap gap-2 justify-between items-center">
              <h2 class="text-base font-medium text-n-slate-12 m-0">
                Заготовленные сообщения
              </h2>
              <Button
                label="Добавить тексты"
                icon="i-lucide-plus"
                size="sm"
                :disabled="!available || saving"
                @click="
                  importError = '';
                  importDialog?.open();
                "
              />
            </div>
            <p class="text-sm text-n-slate-11 m-0">
              Одна строка — одно сообщение, до 150 символов. Выбранный для
              задания текст убирается из доступного списка.
            </p>
            <Input
              v-model="search"
              type="search"
              placeholder="Поиск по текстам"
              aria-label="Поиск по текстам"
            />
            <ul
              v-if="pagedMessages.length"
              class="list-none m-0 p-0 divide-y divide-n-weak border border-n-weak rounded-xl"
            >
              <li
                v-for="item in pagedMessages"
                :key="item.id"
                class="p-4 flex gap-3 justify-between"
              >
                <span class="break-words min-w-0">{{ item.text }}</span
                ><span class="text-xs text-n-slate-11 shrink-0"
                  >{{ item.text.length }}/150</span
                >
              </li>
            </ul>
            <p v-else class="text-sm text-n-slate-11 py-8 text-center">
              {{
                search
                  ? 'Ничего не найдено. Измените запрос.'
                  : 'В этой группе пока нет заготовленных сообщений.'
              }}
            </p>
            <div class="flex gap-3 items-center">
              <Button
                label="Назад"
                size="sm"
                variant="outline"
                :disabled="page <= 1"
                @click="page -= 1"
              /><span class="text-xs text-n-slate-11"
                >{{ page }} / {{ pageCount }}</span
              ><Button
                label="Вперёд"
                size="sm"
                variant="outline"
                :disabled="page >= pageCount"
                @click="page += 1"
              />
            </div>
          </section>
          <section
            v-else-if="tab === 'workers'"
            class="flex flex-col gap-4"
            aria-label="Исполнители UNU"
          >
            <h2 class="text-base font-medium text-n-slate-12 m-0">
              Исполнители группы
            </h2>
            <p class="text-sm text-n-slate-11 m-0">
              Одно выполнение на человека за календарный период
              {{ groupSettings.period_weeks }} нед. Ограничение действует только
              в этой группе. На границе периодов повтор возможен раньше; это не
              скользящий перерыв после отчёта.
            </p>
            <div
              v-for="worker in workers"
              :key="worker.id"
              class="border border-n-weak rounded-xl p-4 text-sm"
            >
              <p class="m-0">Исполнитель UNU №{{ worker.id }}</p>
              <p class="text-xs text-n-slate-11 m-0">
                Отчётов: {{ worker.count }}. Последний:
                {{ new Date(worker.last_at).toLocaleString('ru-RU') }}
              </p>
            </div>
            <p
              v-if="!workers.length"
              class="text-sm text-n-slate-11 py-8 text-center"
            >
              Исполнителей с отчётами пока нет.
            </p>
          </section>
          <section
            v-else
            class="flex flex-col gap-5 max-w-2xl"
            aria-label="Настройки UNU"
          >
            <h2 class="text-base font-medium text-n-slate-12 m-0">
              Настройки группы
            </h2>
            <label class="flex gap-2 items-center text-sm"
              ><input
                v-model="groupSettings.enabled"
                type="checkbox"
                :disabled="!available || saving"
              />Включить задания UNU для группы</label
            >
            <label class="flex flex-col gap-2 text-sm"
              >Одно выполнение за период<select
                v-model="groupSettings.period_weeks"
                :disabled="!available || saving"
              >
                <option v-for="week in [1, 2, 3, 4]" :key="week" :value="week">
                  {{ week }} нед.
                </option>
              </select></label
            >
            <p class="text-xs text-n-slate-11 m-0">
              Периоды фиксированные, считаются от понедельника 5 января 2026
              года по Москве. Для каждой группы используется отдельная папка
              UNU. Глобальный чёрный список не меняется.
            </p>
            <Input
              v-model="groupSettings.poll_seconds"
              type="number"
              min="30"
              max="3600"
              label="Интервал проверки отчётов, секунд"
              :disabled="!available || saving"
            />
            <div class="flex flex-wrap gap-2">
              <Button
                label="Сохранить настройки"
                size="sm"
                :disabled="!available || saving"
                @click="saveSettings"
              /><Button
                label="Синхронизировать"
                size="sm"
                variant="outline"
                :disabled="!available || saving"
                @click="sync"
              />
            </div>
            <div class="border-t border-n-weak pt-4 text-sm text-n-slate-11">
              <h3 class="text-sm font-medium text-n-slate-12">
                Состояние подключения
              </h3>
              <p>
                {{
                  configured
                    ? 'Подключение UNU настроено на сервере.'
                    : 'UNU не подключён.'
                }}
              </p>
              <p>
                Баланс: {{ balanceLabel }}. Минимальная цена: {{ priceLabel }}.
              </p>
              <p v-if="connection?.last_worker_at">
                Фоновая проверка:
                {{
                  new Date(connection.last_worker_at).toLocaleString('ru-RU')
                }}.
              </p>
              <Banner v-if="connection?.error_code" color="ruby">
                {{ failureText(connection.error_code) }}
              </Banner>
              <Button
                label="Обновить данные"
                size="sm"
                variant="outline"
                :disabled="saving"
                @click="bootstrap"
              />
            </div>
          </section>
        </template>
      </div>
    </template>
    <Dialog
      ref="taskDialog"
      :title="editingId ? 'Изменить задание' : 'Новое задание UNU'"
      description="Одно выполнение. Стоимость и сроки указаны в параметрах. После передачи в UNU заказ может расходовать баланс."
      confirm-button-label="Сохранить задание"
      cancel-button-label="Отмена"
      :disable-confirm-button="!available || saving"
      :is-loading="saving"
      width="xl"
      overflow-y-auto
      @confirm="saveTask"
    >
      <div class="flex flex-col gap-4">
        <Banner color="blue">
          Одно выполнение — {{ priceLabel }}. Сохранение задания разрешает
          создать заказ в UNU не раньше указанного времени.
        </Banner>
        <Banner v-if="formError" role="alert" color="ruby">
          {{ formError }}
        </Banner>
        <p class="text-sm text-n-slate-11 m-0 break-words">
          {{ group?.title || 'Выберите группу' }}
        </p>
        <label class="flex flex-col gap-2 text-sm"
          >Выбрать заготовку<select
            v-model="form.messageId"
            @change="pickMessage"
          >
            <option value="">Ввести свой текст</option>
            <option v-for="item in messages" :key="item.id" :value="item.id">
              {{ item.text }}
            </option>
          </select></label
        >
        <TextArea
          id="unu-task-text"
          v-model="form.text"
          :max-length="150"
          label="Текст задания"
          placeholder="Например: задать вопрос по уходу за волосами"
          :rows="4"
        />
        <p class="text-xs text-n-slate-11 m-0">
          {{ form.text.length }} / 150 символов
        </p>
        <Input
          v-model="form.plannedAt"
          type="datetime-local"
          label="Начать не раньше, Москва (UTC+3)"
        />
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label class="flex flex-col gap-2 text-sm"
            >Пол исполнителя<select v-model="form.gender">
              <option value="0">Любой</option>
              <option value="1">Женский</option>
              <option value="2">Мужской</option>
            </select></label
          ><Input
            v-model="form.age"
            type="number"
            min="16"
            max="60"
            label="Возраст от"
            placeholder="Без ограничения"
          />
        </div>
        <Input
          :model-value="`https://t.me/${group?.username?.replace(/^@/, '')}`"
          label="Ссылка группы"
          readonly
        />
        <details class="border border-n-weak rounded-lg p-3 text-sm">
          <summary class="cursor-pointer text-n-slate-12">
            Параметры заказа UNU
          </summary>
          <dl class="grid grid-cols-2 gap-2 mt-3 text-xs text-n-slate-11">
            <dt>Площадка</dt>
            <dd>Telegram</dd>
            <dt>Тип</dt>
            <dd>Короткие комментарии, до 150 символов</dd>
            <dt>Тариф</dt>
            <dd>21</dd>
            <dt>Папка</dt>
            <dd>По выбранной группе</dd>
            <dt>Страна</dt>
            <dd>Россия</dd>
            <dt>Цена</dt>
            <dd>{{ priceLabel }} за выполнение</dd>
            <dt>Количество</dt>
            <dd>1 выполнение</dd>
            <dt>На выполнение</dt>
            <dd>2 часа</dd>
            <dt>На проверку</dt>
            <dd>72 часа</dd>
            <dt>Отчёт</dt>
            <dd>Скриншот выполнения</dd>
          </dl>
          <p class="text-xs text-n-slate-11 mb-0">
            Стоимость сверяется с текущим тарифом перед отправкой. При обрыве
            связи заказ автоматически не дублируется.
          </p>
        </details>
      </div>
    </Dialog>
    <Dialog
      ref="importDialog"
      title="Добавить тексты"
      description="Одна строка — одно сообщение. До 500 строк за один раз."
      confirm-button-label="Добавить тексты"
      cancel-button-label="Отмена"
      :disable-confirm-button="!available || saving"
      :is-loading="saving"
      overflow-y-auto
      @confirm="addTexts"
    >
      <Banner v-if="importError" role="alert" color="ruby">
        {{ importError }}
      </Banner>
      <TextArea
        id="unu-import-text"
        v-model="importText"
        :max-length="75500"
        label="Тексты сообщений"
        :rows="8"
        placeholder="Первое сообщение&#10;Второе сообщение"
      />
    </Dialog>
    <Dialog
      ref="historyDialog"
      title="История и отчёт задания"
      description="Состояние заказа и отчёты исполнителей из UNU."
      :show-confirm-button="false"
      cancel-button-label="Закрыть"
      overflow-y-auto
    >
      <div
        v-if="selectedTask"
        class="flex flex-col gap-4 text-sm text-n-slate-12"
      >
        <p class="break-words m-0">{{ selectedTask.text }}</p>
        <dl class="grid grid-cols-2 gap-3">
          <dt>Дата, МСК</dt>
          <dd>{{ localTime(selectedTask.plannedAt) }}</dd>
          <dt>Статус</dt>
          <dd>{{ statuses[selectedTask.state] }}</dd>
          <dt>Номер в UNU</dt>
          <dd>{{ selectedTask.provider_task_id || 'Ещё не передано' }}</dd>
          <dt>Отчёт и скриншот</dt>
          <dd>
            {{
              history?.reports.length
                ? `${history.reports.length} отчёт(ов)`
                : 'Отчётов пока нет'
            }}
          </dd>
        </dl>
        <Banner v-if="selectedTask.error_code" color="ruby">
          {{ selectedTask.error_code }}
        </Banner>
        <p v-if="historyLoading" role="status">Получаем историю…</p>
        <div
          v-for="report in history?.reports || []"
          :key="report.id"
          class="border border-n-weak p-3 rounded-lg"
        >
          <p>
            Отчёт №{{ report.provider_report_id }} —
            {{ reportStates[report.status] || report.status }}
          </p>
          <p class="whitespace-pre-wrap break-words">{{ report.text }}</p>
          <a
            v-for="(file, index) in report.files"
            :key="file"
            :href="file"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-blue-text block"
            >Вложение {{ index + 1 }}</a
          >
        </div>
        <ol class="text-xs text-n-slate-11">
          <li v-for="event in history?.events || []" :key="event.id">
            {{ new Date(event.at).toLocaleString('ru-RU') }} —
            {{ eventLabels[event.code] || 'Обновление заказа' }}
          </li>
        </ol>
        <p v-if="selectedTask.legacy_id" class="text-xs text-n-slate-11">
          История перенесена из старой системы. Повторный заказ не создаётся.
        </p>
      </div>
    </Dialog>
  </SettingsLayout>
</template>
